import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./shopMapPreview.css"

// Fix default marker icon (Leaflet quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
	iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
	shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const MAPBOX_TOKEN = process.env.REACT_APP_MAP_BOX_TOKEN;
const PREVIEW_ZOOM = 15;
const TILE_RETRY_LIMIT = 2;

/* Built once. Rebuilding the icon on every render makes Leaflet drop the marker
   and re-add it each time the host page re-renders — the pin visibly blinks. */
const previewMarkerIcon = L.divIcon({
	className: "sf-marker",
	html: `
		<div class="sf-marker-inner no-rating">
			<svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M18 0C8.1 0 0 8.1 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.1 27.9 0 18 0z"
					fill="#111"
				/>
			</svg>
		</div>
	`,
	iconSize: [36, 48],
	iconAnchor: [18, 48],
});

const toCoord = (value) => {
	if (value === null || value === undefined || value === "") return null;

	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : null;
};

/* Mapbox drops the odd tile (rate limit, flaky connection) and Leaflet never asks
   for it again, so the gap stays grey for as long as the page is open. */
const retryTile = (event) => {
	const tile = event.tile;
	if (!tile) return;

	const attempt = Number(tile.dataset.sfRetry || 0) + 1;
	if (attempt > TILE_RETRY_LIMIT) return;

	tile.dataset.sfRetry = String(attempt);

	// Cache-bust, otherwise the browser can just replay the failed response.
	const url = tile.src.replace(/[?&]sfRetry=\d+/, "");
	const separator = url.includes("?") ? "&" : "?";

	window.setTimeout(() => {
		tile.src = `${url}${separator}sfRetry=${attempt}`;
	}, 400 * attempt);
};

/* Leaflet measures its container once, when the map is created, and only re-measures
   on a window resize. Every preview here sits in a sticky sidebar that is still
   settling at creation time, so that first measure comes back short and tiles are
   only requested for the strip Leaflet thinks exists — the half-loaded map.
   Re-measure on mount and whenever the container really changes size or first shows. */
const PreviewMapController = ({ lat, lng }) => {
	const map = useMap();

	useEffect(() => {
		const container = map.getContainer();
		const invalidate = () => map.invalidateSize({ animate: false });

		// Measure now, on the next frame, and once the layout has settled. rAF alone
		// is not enough: it never fires while the tab is in the background.
		invalidate();
		const frame = requestAnimationFrame(invalidate);
		const settleTimer = window.setTimeout(invalidate, 300);

		const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(invalidate) : null;
		resizeObserver?.observe(container);

		// Sidebar previews usually start below the fold — measure again once they show.
		const visibilityObserver =
			typeof IntersectionObserver === "function"
				? new IntersectionObserver((entries) => {
						if (entries.some((entry) => entry.isIntersecting)) invalidate();
				  })
				: null;
		visibilityObserver?.observe(container);

		// A page opened in a background tab is never laid out, so nothing above fires until it is shown.
		document.addEventListener("visibilitychange", invalidate);

		return () => {
			cancelAnimationFrame(frame);
			window.clearTimeout(settleTimer);
			resizeObserver?.disconnect();
			visibilityObserver?.disconnect();
			document.removeEventListener("visibilitychange", invalidate);
		};
	}, [map]);

	// `center` is read once, at creation: without this an address edit leaves the old pin on screen.
	useEffect(() => {
		map.setView([lat, lng], PREVIEW_ZOOM, { animate: false });
	}, [map, lat, lng]);

	return null;
};

const ShopMapPreview = ({ lat, lng }) => {
	const latitude = toCoord(lat);
	const longitude = toCoord(lng);

	if (latitude === null || longitude === null) return null;

	return (
		<MapContainer
			className="sf-map-preview"
			center={[latitude, longitude]}
			zoom={PREVIEW_ZOOM}
			style={{ height: 200, width: "100%", borderRadius: 12 }}
			zoomControl={false}
			dragging={false}
			scrollWheelZoom={false}
			doubleClickZoom={false}
			touchZoom={false}
			boxZoom={false}
			keyboard={false}
			attributionControl={false}
		>
			{MAPBOX_TOKEN ? (
				<TileLayer
					url={`https://api.mapbox.com/styles/v1/iwhite/cmjwicu52001001qo18qxh9i4/tiles/512/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
					tileSize={512}
					zoomOffset={-1}
					updateWhenIdle={false}
					eventHandlers={{ tileerror: retryTile }}
				/>
			) : (
				<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" eventHandlers={{ tileerror: retryTile }} />
			)}

			<Marker
				position={[latitude, longitude]}
				icon={previewMarkerIcon}
			/>

			<PreviewMapController lat={latitude} lng={longitude} />
		</MapContainer>
	);
};


export default ShopMapPreview;
